import styles from "./DetailVenueCard.module.css"
import React from 'react';
import { addDPH } from "../../../utils/dph";
import { MapPinned, Users, ExternalLink } from 'lucide-react';
import { VenueProductCardProps } from "../../../types/venue.types"
import GalleryCard from "../../Cards/GalleryCard/GalleryCard";
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import CloseButton from "../../Button/CloseButton/CloseButton";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { useOrderContext } from "../../../context/OrderContext";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { normalizeDate, denormalizeDate } from "../../../utils/calendar";
import { usePopup } from "../../../context/PopupContext";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import { defaultOrderTime } from "../../../constants/times";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
/**
* Detail card for venues
*
* @param props VenueProductCardProps
* @param {number} props.id - Venue id
* @param {string} props.title - Venue title
* @param {string} props.address - Venue address
* @param {number} props.price - Venue price
* @param {string} props.photo - Venue photo URL
* @param {string} props.type - Venue type
* @param {number} props.amountOfPeople - Recommended amount of people
* @param {string} props.description - Venue description
* @returns TSX.Element
*/
const DetailVenueCard = ({ id, title, price, photo, type, address, amountOfPeople, description = "" }: VenueProductCardProps) => {
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t } = useTranslation(); // Translation
    const { orderData, orderType } = useOrderContext(); // Order data
    const {closeDetail} = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const { open: openPopup, close: closePopup, setPopupTarget, setOpenOnTop: setPopupOpenOnTop } = usePopup();
    const {isPublicView} = usePublicCatalog();

    const dateKey = denormalizeDate(orderData.selectedDate);
    const dateISO = normalizeDate(orderData.selectedDate);
    const { cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id,
            category: "venue",
            dateKey,
            dateISO,
        },
    });

    // Turn the addres into uri safe format
    const encodedAddress = encodeURIComponent(address);
    const mapSrc = `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
    const mapLink = `https://www.google.com/maps?q=${encodedAddress}`;
    // Make sure price is always in correct currency
    const no_dph_price = czk_to_eur(price, czk_eur_rate, selectedCurrency).toFixed(2);
    const dph_price = czk_to_eur(addDPH(price), czk_eur_rate, selectedCurrency).toFixed(2);
    
    return(
        <>
            <div className={styles.ProductDetail} key={id}>
                <div className={styles.ProductDetailBlur} onClick={closeDetail}></div>
                <div className={styles.ProductDetailCard}>
                    <div className={styles.ProductMain}>
                        <GalleryCard imgSrc={photo} className={styles.ProductImage} onPopup={true} />
                        <div className={styles.ProductMainText}>
                            <div className={styles.ProductMainInfo}>
                                <p className={styles.ProductTitle}>{title}</p>
                                <div className={styles.ProductSpecs}>
                                    <div className={styles.ProductSpec}>
                                        <MapPinned/>
                                        <p>{type}</p>
                                    </div>
                                    <div className={styles.ProductSpec}>
                                        <Users/>
                                        <p>{t("venue.capacity")} {amountOfPeople} {t("products.people")}</p>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.ProductMainActions}>
                                <div className={styles.ProductPrices}>
                                    <p className={styles.NoDphPrice}>{no_dph_price} { selectedCurrency === "eur"? "EUR" : "Kč"}/<Trans i18nKey="products.no_dph" components={{ 1: <span/> }} /></p>
                                    <p className={styles.DphPrice}>{dph_price} { selectedCurrency === "eur"? "EUR" : "Kč"} {t("products.dph")}</p>
                                </div>
                                {cartAmount>0 ? (
                                    <CartAmountSecondary amountInCart={cartAmount} 
                                    onIncrease={() => handleCart(cartAmount + 1)}
                                    onDecrease={() => handleCart(Math.max(cartAmount - 1, 0))}
                                    disabled={isPublicView}
                                    />
                                ) : (
                                    <CartButtonSecondary text={t("buttons.demand")} 
                                        onClick={()=>{ 
                                            if(orderType !== "demand"){
                                                setPopupOpenOnTop(true);
                                                setPopupTarget(
                                                    {
                                                        title: t("demand_popup.title"),
                                                        content: t("demand_popup.content"),
                                                        submitBtnText: t("demand_popup.continue"),
                                                        backBtnText: t("demand_popup.back"),
                                                        onClose: (()=>{closePopup()}),
                                                        onSubmit: (() => {
                                                            openTimeModal(
                                                                {
                                                                    type: "single",
                                                                    date: orderData.selectedDate,
                                                                    time: defaultOrderTime,
                                                                    productId: id,
                                                                    prodType: "venue",
                                                                    static: true,
                                                                },
                                                                { openOnTop: true }
                                                            );
                                                            closePopup();
                                                        })
                                                    }
                                                )
                                                openPopup()
                                            }else{
                                                openTimeModal(
                                                    {
                                                        type: "single",
                                                        date: orderData.selectedDate,
                                                        time: defaultOrderTime,
                                                        productId: id,
                                                        prodType: "venue",
                                                        static: true,
                                                    },
                                                    { openOnTop: true }
                                                );
                                            }
                                        }}
                                        className={styles.DetailCartButton}
                                        disabled={isPublicView}
                                        />
                                )}
                                
                            </div>
                        </div>
                    </div>
                    <div className={styles.ProductInfo}>
                        <p className={styles.InfoTitle}>{t("products.desc")}</p>
                        <p className={styles.InfoDescription}>
                            {/*Format the description */}
                            {description.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                {line}
                                <br />
                                </React.Fragment>
                            ))}
                        </p>
                    </div>
                    <div className={styles.ProductAddressText}>
                        <p className={styles.ProductAddressTitle}>{t("venue.address")}</p>
                        <p className={styles.ProductAddressDesc}>{address}</p>
                    </div>
                    <div className={styles.ProductAddressMap}>
                        <div className={styles.MapCenter}>
                            <iframe
                                title="Google Map"
                                src={mapSrc}
                                width="100%"
                                height="460"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                                className={styles.Map}
                            ></iframe>
                            <a href={mapLink} target="_blank">
                                <div className={styles.MapLink}>
                                    <p>{t("venue.see_maps")}</p>
                                    <ExternalLink/>
                                </div>
                            </a>
                        </div>
                    </div>
                    <CloseButton onClick={()=>closeDetail && closeDetail()} className={styles.Close}/>
                </div>
            </div>
        </>
    )
};

export default DetailVenueCard;
